import React, { useState, useEffect, useContext } from "react";

import axios from "../../api/axios";

import { toast } from "react-toastify";
import PopUpPanel from "../../components/PopUpPanel/PopUpPanel";
import FormField from "../../components/forms/FormField/FormField";
import { CampaignContext } from "../../contexts/CampaignContext";
import HMSTimeField from "../../components/forms/HMSTimeField/HMSTimeField";
import FormDropDown from "../../components/forms/DropDownList/FormDropDown";
import RadioButtonGroup from "../../components/forms/RadioButtonGroup/RadioButtonGroup";

import { getDefaultTimeZoneListData, convertFormDateTimeTimezoneToUnixTimestamp, getTzAdjustedDateObject } from "../../helpers/Dates";

import DateSelect from "../../components/forms/DateSelect/DateSelect";
import SelectUser from "../../components/SelectUser/SelectUser";
import { updateStreamRoute, archiveStreamRoute } from "../../api/routes";
import { getTimezoneOptions, parseTimezone } from "../../helpers/TimeZones";
import { IconJsxer } from "../../helpers/IconHelper";
import { buildAmazonStreamChannel, buildTwitchStreamChannel, getStreamChannelId, getStreamChannelLabel, getStreamChannelProfilePic } from "../../helpers/StreamChannels";

const EditStream = (props) => {
    const [campaignContext, setCampaignContext] = useContext(CampaignContext);
    const streamData = props.stream;
    const twitchLogoSrc = `${process.env.PUBLIC_URL || ''}/assets/icons/twitch_logo.png`;
    const amazonLogoSrc = `${process.env.PUBLIC_URL || ''}/assets/icons/amazon_live.png`;
    const inferDefaultChannelPlatform = () => {
        if (campaignContext.campaignData?.platform === 'Amazon') return 'amazon';
        if (campaignContext.campaignData?.platform === 'Twitch') return 'twitch';
        return (streamData.channels || []).some((channel) => channel?.isAmazon) ? 'amazon' : 'twitch';
    };
    const defaultChannelPlatformValue = campaignContext.campaignData?.platform === 'Amazon'
        ? 'amazon'
        : inferDefaultChannelPlatform();

    const [showSelectUser, setShowSelectUser] = useState(false);
    const toggleShowSelectUser = (e) => {
        if (e) {
            e.preventDefault();
        }
        setShowSelectUser(!showSelectUser);
    }
    const userSelected = (user) => {
        console.log('User selected:', user);
        // Add user to streamers list
        const channelName = user.twitchHandle || user.name;
        console.log('Channel name:', channelName);
        const newChannel = buildTwitchStreamChannel(channelName);
        if (!streamers.find(s => getStreamChannelId(s) === getStreamChannelId(newChannel))) {
            // Fetch profile pic
            console.log('Fetching profile pic for user:', channelName);
            props._getProfilePicFunc(channelName, (profilePic) => {
                console.log('Received profile pic:', profilePic);
                setStreamers([...streamers, {
                    ...buildTwitchStreamChannel(channelName, profilePic),
                    discord: user.discord || '',
                }]);
            });
        }
        setShowSelectUser(false);
    }

    const [streamName, setStreamName] = useState(streamData.name || '');
    const [streamNameError, setStreamNameError] = useState('');
    
    const streamTypeOptions = [
        { label: 'Live stream', value: false, index: 0 },
        { label: 'Live test', value: true, index: 1 }
    ];
    const [streamType, setStreamType] = useState(
        streamData.demoMode ? streamTypeOptions[1] : streamTypeOptions[0]
    );
    
    const [streamers, setStreamers] = useState(streamData.channels || []);
    const [streamersError, setStreamersError] = useState('');
    const [manualChannelName, setManualChannelName] = useState('');
    const channelPlatformOptions = [
        {
            label: <span className="platform-option-label"><img src={twitchLogoSrc} alt="Twitch" className="platform-option-icon" /></span>,
            value: 'twitch',
            index: 0,
            id: 'edit_stream_platform_twitch',
        },
        {
            label: <span className="platform-option-label"><img src={amazonLogoSrc} alt="Amazon" className="platform-option-icon" /></span>,
            value: 'amazon',
            index: 1,
            id: 'edit_stream_platform_amazon',
        },
    ];
    const [channelPlatform, setChannelPlatform] = useState(defaultChannelPlatformValue);

    const removeStreamer = (index) => {
        setStreamers(streamers.filter((_, i) => i !== index));
    };
    
    const addManualChannel = (e) => {
        e.preventDefault();
        const trimmedChannelName = manualChannelName.trim();
        if (!trimmedChannelName) {
            return;
        }

        if (channelPlatform === 'amazon') {
            const newChannel = buildAmazonStreamChannel(trimmedChannelName);
            if (!streamers.find(s => getStreamChannelId(s) === getStreamChannelId(newChannel))) {
                setStreamers([...streamers, newChannel]);
                setManualChannelName('');
            }
            return;
        }

        const newChannel = buildTwitchStreamChannel(trimmedChannelName);
        if (!streamers.find(s => getStreamChannelId(s) === getStreamChannelId(newChannel))) {
            console.log('Fetching profile pic for manual channel:', trimmedChannelName);
            props._getProfilePicFunc(trimmedChannelName, (profilePic) => {
                console.log('Received profile pic:', profilePic);
                setStreamers([...streamers, buildTwitchStreamChannel(trimmedChannelName, profilePic)]);
                setManualChannelName('');
            });
        }
    };
    
    const [streamerHandle, setStreamerHandle] = useState(streamData.twitchHandle);
    const [streamerHandleError, setStreamerHandleError] = useState('');
    const [streamDiscord, setStreamDiscord] = useState(streamData.discord);
    const [streamDiscordError, setStreamDiscordError] = useState('');

    // Turn our startTime / endTime into editable data...
    // Getting timezone caches the timezone data, so we do that first so we know parseTimezone is working with the correct data
    const [tzOptions, setTzOptions] = useState(getTimezoneOptions(streamData.startTime));

    // First make sure we have our timezone data sorted!
    const timezoneData = parseTimezone(streamData.timeZone);
    const timezoneDiffSecs = timezoneData.offset * 3600;

    const [streamDate, setStreamDate] = useState(null);
    if (streamDate === null) {
        // We need to transform the startTime into date that is 00:00 in the stream's timezone
        // First get the date adjusted for the stream's timezone
        let startDate = getTzAdjustedDateObject(streamData.startTime, timezoneData.offset);
        // Now create a new Date object at midnight UTC for just the date part
        // This avoids double timezone adjustments
        startDate = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0));
        setStreamDate(startDate);
    }
    const dateSelected = (id, val) => setStreamDate(val);

    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    if (startTime === null) {
        // We need to reduce startTime / date down to 24 hours in seconds, currently it is milliseconds from Jan 1st 1970
        let startTimeOfDay = streamData.startTime / 1000 + timezoneDiffSecs;
        while (startTimeOfDay > 3600 * 24) {
            startTimeOfDay -= 3600 * 24;
        }
        setStartTime(startTimeOfDay);
    }
    if (endTime === null) {
        // We need to reduce endTime / date down to 24 hours in seconds, currently it is milliseconds from Jan 1st 1970
        let endTimeOfDay = streamData.endTime / 1000 + timezoneDiffSecs;
        while (endTimeOfDay > 3600 * 24) {
            endTimeOfDay -= 3600 * 24;
        }
        setEndTime(endTimeOfDay);
    }

    const reevaluateTimezoneOptions = (selectedTimeZone = null) => {
        const utcUnix_startTime = convertFormDateTimeTimezoneToUnixTimestamp(streamDate, startTime, selectedTimeZone || timezone);
        const evaluatedOptions = getTimezoneOptions(utcUnix_startTime);
        if (tzOptions !== evaluatedOptions) {
            const currentTimezone = selectedTimeZone || timezone;
            setTzOptions(evaluatedOptions);
            // Settings TzOptions can re-order the list!
            setTimezone(parseTimezone(currentTimezone.value));
        }
    }

    const [timezone, setTimezone] = useState(getDefaultTimeZoneListData(tzOptions, streamData.timeZone));
    const selectTimeZone = (e, id, item) => {
        reevaluateTimezoneOptions(item);
    }

    useEffect(() => {
        reevaluateTimezoneOptions();
    }, [streamDate, startTime]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const getChannelTypeIcon = (channel) => (channel?.isAmazon ? amazonLogoSrc : twitchLogoSrc);

    const saveStream = (e) => {
        e.preventDefault();

        if(!isSubmitting)
        {
            let formValid = true;
            
            if (streamName === '') {
                setStreamNameError('You need to enter a stream name');
                formValid = false;
            } else {
                setStreamNameError('');
            }
            
            if (streamers.length === 0) {
                setStreamersError('You need to add at least one streamer');
                formValid = false;
            } else {
                setStreamersError('');
            }

            if (formValid) {
                let utcUnix_startTime = convertFormDateTimeTimezoneToUnixTimestamp(streamDate, startTime, timezone);
                let normalizedEndTime = endTime;
                if (normalizedEndTime < startTime) {
                    normalizedEndTime += 86400;
                }
                let utcUnix_endTime = convertFormDateTimeTimezoneToUnixTimestamp(streamDate, normalizedEndTime, timezone);

                if(!isSubmitting)
                {
                    // do axios post here
                    setIsSubmitting(true);
                    axios
                    .post(updateStreamRoute, { 
                        campaignID:props.campaignId,
                        _id:streamData._id,
                        name: streamName,
                        demoMode: streamType.value,
                        channels: streamers,
                        twitchHandle:streamerHandle,
                        discord:streamDiscord,
                        startTime:utcUnix_startTime,
                        endTime:utcUnix_endTime,
                        timeZone:timezone.value, 
                        withCredentials:true
                    })
                    .then((response) =>{
                        console.log(response)

                        let oldCampaignData = campaignContext.campaignData
                        oldCampaignData.streams = response.data.campaignData.streams;
                        setIsSubmitting(false);
                        setCampaignContext((oldValues) => {
                            return { ...oldValues, initialising: false, campaignData: oldCampaignData }
                        })

                        toast.success("Stream ("+streamName+") successfully updated");
                        props.closePanelFunc();
                    })
                }
                
            }
        }
    }

    const handleDeleteStream = () => {
        axios
            .post(archiveStreamRoute, {
                _id: streamData._id,
                campaignID: props.campaignId,
                archived: streamData.archived,
                withCredentials: true
            })
            .then((response) => {
                let updatedCampaignData = campaignContext.campaignData;
                updatedCampaignData.streams = response.data.campaignData.streams;
                setCampaignContext((oldValues) => {
                    return { ...oldValues, initialising: false, campaignData: updatedCampaignData }
                });
                toast.success('Stream successfully archived');
                setShowDeleteConfirm(false);
                props.closePanelFunc();
            })
            .catch((error) => {
                toast.error('Failed to archive stream');
                setShowDeleteConfirm(false);
            });
    };

    return (
        <>
        {showDeleteConfirm && (
            <PopUpPanel closePanelFunc={() => setShowDeleteConfirm(false)} showCloseBtn={false}>
                <div className="delete-confirm-popup">
                    <h3>Delete Stream</h3>
                    <p>Are you sure you want to delete this stream?</p>
                    <div className="delete-confirm-buttons">
                        <button className="confirm-no-btn" onClick={() => setShowDeleteConfirm(false)}>No</button>
                        <button className="confirm-yes-btn" onClick={handleDeleteStream}>Yes</button>
                    </div>
                </div>
            </PopUpPanel>
        )}
        {!showSelectUser && !showDeleteConfirm &&
        <PopUpPanel closePanelFunc={props.closePanelFunc}>
            <div className="form-holder co-popup-content">
                <form id="editstream" className="form">
                    <div className="edit-stream-header">
                        <h2>Edit stream</h2>
                    </div>                    <FormField
                        type="text"
                        id="stream_name"
                        placeholder="HBO stream"
                        setFunc={setStreamName}
                        label="Stream name*"
                        error={streamNameError}
                        value={streamName}
                    />
                    
                    <FormDropDown
                        id="stream_type"
                        label="Stream type"
                        showLabel={true}
                        items={streamTypeOptions}
                        currentIndex={streamType.index}
                        selectFunc={(e, id, item) => setStreamType(item)}
                    />
                    
                    <div className="label">Day</div>
                    <div className="fl-row drop-gap grow">
                        <DateSelect 
                            id="stream_date" 
                            allowPast={true}
                            initialDate={streamDate}
                            selectFunc={dateSelected}
                        />
                    </div>
                    
                    <div className="label">Time</div>
                    <div className="fl-row time-row-inline">
                    <HMSTimeField
                        label="Start"
                        inlineLabel={true}
                        showLabel={false}
                        id="stream_start"
                        value={startTime}
                        setFunc={setStartTime}
                        maxHours={23}
                        showSeconds={false}
                        relativeTime={false}
                        narrow={true}
                        centered={false}
                        showError={false}
                    />
                    <div className="time-separator">to</div>
                    <HMSTimeField
                        label="Until"
                        inlineLabel={true}
                        showLabel={false}
                        id="stream_end"
                        value={endTime}
                        setFunc={setEndTime}
                        maxHours={23}
                        showSeconds={false}
                        relativeTime={false}
                        narrow={true}
                        centered={false}
                        showError={false}
                    />
                    <FormDropDown
                        id="timezone"
                        label="Timezone"
                        showLabel={false}
                        items={tzOptions}
                        currentIndex={timezone.index}
                        selectFunc={selectTimeZone}
                    />
                    </div>
                    
                    <div className="label">Channels</div>
                    {streamersError && <div className="error-text">{streamersError}</div>}
                    <div className="streamers-list">
                        {streamers.map((streamer, index) => (
                            <div key={getStreamChannelId(streamer) || index} className="streamer-item">
                                <div className="streamer-info">
                                    <img src={getStreamChannelProfilePic(streamer)} alt={getStreamChannelLabel(streamer)} className="streamer-avatar" />
                                    <span>{getStreamChannelLabel(streamer)}</span>
                                </div>
                                <div className="streamer-actions">
                                    <img src={getChannelTypeIcon(streamer)} alt={streamer?.isAmazon ? 'Amazon' : 'Twitch'} className="streamer-connection-icon" />
                                    <button 
                                        type="button"
                                        className="remove-streamer-btn"
                                        onClick={() => removeStreamer(index)}
                                        aria-label={`Remove ${getStreamChannelLabel(streamer)}`}
                                    >
                                        {IconJsxer.GetIcon(IconJsxer.ICONS.trash)}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="add-streamer-section">
                        <div className="manual-channel-input">
                            <RadioButtonGroup
                                id="channel_platform"
                                label={null}
                                showLabel={false}
                                className="channel-platform-toggle"
                                options={channelPlatformOptions}
                                selectedOption={channelPlatform}
                                selectFunc={(e, id, value) => setChannelPlatform(value)}
                            />
                            <input
                                type="text"
                                placeholder={channelPlatform === 'amazon' ? 'Enter Amazon broadcast ID' : 'Enter Twitch channel name'}
                                value={manualChannelName}
                                onChange={(e) => setManualChannelName(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        addManualChannel(e);
                                    }
                                }}
                            />
                            <button 
                                type="button"
                                className="add-manual-btn"
                                onClick={addManualChannel}
                                disabled={!manualChannelName.trim()}
                            >
                                Add
                            </button>
                        </div>
                        {channelPlatform === 'twitch' && (
                            <button 
                                type="button"
                                className="add-streamer-btn" 
                                onClick={toggleShowSelectUser}
                            >
                                {IconJsxer.GetIcon(IconJsxer.ICONS.plus)}
                                Add from user list
                            </button>
                        )}
                    </div>
                    
                    <button className={'submit-btn' + (isSubmitting ? ' button-inactive' : ' button-active')} onClick={isSubmitting ? null : saveStream}>
                        {isSubmitting ? "Updating stream" : "Confirm changes"}
                    </button>
                    
                    <div className="delete-stream-section">
                        <span className="delete-stream-link" onClick={() => setShowDeleteConfirm(true)}>Delete stream</span>
                    </div>
                </form>
            </div>
        </PopUpPanel>
        }
        {showSelectUser &&
            <SelectUser closePanelFunc={toggleShowSelectUser} selectUserFunc={userSelected} twitchOnly={true} />
        }
        </>
    )
}

export default EditStream;
